"use client";

// The company's website disclaimer, shown as a blocking dialog when a reader
// asks to view or download a gated offer document (Abridged Prospectus, Draft
// Red Herring Prospectus) — NOT on page load. Confirming releases the pending
// document; declining swaps the dialog for the "not permitted" message and the
// document stays closed. Escape and backdrop clicks deliberately do nothing.
//
// The dialog is controlled by its parent (InvestorTabs decides when a click
// needs it). Nothing is remembered between clicks by design: each gated
// document asks again.
//
// Copy lives in lib/investorDisclaimer.ts and every line is overridable from
// /admin/pages → Investor Relations through the `disclaimer.*` keys. Because
// the dialog no longer renders on load, the admin preview opens it
// automatically in edit mode so those fields stay reachable in place.
import { useEffect, useState } from "react";
import {
  DISCLAIMER_TITLE,
  DISCLAIMER_LEAD,
  DISCLAIMER_PARAS,
  DISCLAIMER_CONFIRM_PARAS,
  DISCLAIMER_CONFIRM_LABEL,
  DISCLAIMER_DECLINE_LABEL,
  DISCLAIMER_BLOCKED,
} from "@/lib/investorDisclaimer";

export function InvestorDisclaimerGate({
  open,
  onConfirm,
  onClose,
  c = {},
}: {
  /** Render the dialog (a gated document click is pending). */
  open: boolean;
  /** Reader confirmed — release the pending document. */
  onConfirm: () => void;
  /** Dialog dismissed without confirming (after declining). */
  onClose: () => void;
  c?: Record<string, string>;
}) {
  const [declined, setDeclined] = useState(false);
  // Admin preview: the dialog only exists after a click, so edit mode opens it
  // by itself — otherwise the disclaimer fields would be unreachable in place.
  const [editing, setEditing] = useState(false);
  const t = (key: string, fallback: string) => c[key]?.trim() || fallback;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // The dialog opens for in-place editing only when the preview asks for it
    // (gate=doc — the section's "show in preview" button). It must NOT open on
    // the plain edit preview: it is an undismissable overlay there, which made
    // the rest of the investor page uneditable in place.
    setEditing(
      params.has("tsedit") && params.get("gate") === "doc" && window.parent !== window,
    );
  }, []);

  const show = open || editing;

  // Ask afresh on every open.
  useEffect(() => {
    if (open) setDeclined(false);
  }, [open]);

  // Hold the page still while the dialog is up. Locked on <html> as well as
  // <body>: iOS Safari ignores overflow:hidden on the body alone.
  useEffect(() => {
    if (!show) return;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [show]);

  if (!show) return null;

  const paras = DISCLAIMER_PARAS.map((p, i) => t(`disclaimer.p.${i}`, p));
  const confirmParas = DISCLAIMER_CONFIRM_PARAS.map((p, i) => t(`disclaimer.confirm.${i}`, p));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="investor-disclaimer-title"
      className="fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto bg-brand-dark/60 p-4 py-8 backdrop-blur-[2px] md:p-8"
    >
      {declined ? (
        <div className="w-full max-w-xl rounded-2xl bg-white p-8 text-center shadow-2xl md:p-12">
          <p className="mono-meta uppercase text-brand-accent-dark">Access restricted</p>
          <p className="mt-5 text-lg leading-relaxed text-brand-ink md:text-xl" data-cms-key="disclaimer.blocked">
            {t("disclaimer.blocked", DISCLAIMER_BLOCKED)}
          </p>
          <button type="button" onClick={onClose} className="btn-secondary btn-sm mt-8 inline-flex">
            Close
          </button>
        </div>
      ) : (
        <div className="flex max-h-[calc(100dvh-4rem)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div className="border-b border-brand-line px-6 py-5 md:px-10 md:py-6">
            <h2
              id="investor-disclaimer-title"
              className="t-h3 text-brand-ink"
              data-cms-key="disclaimer.title"
            >
              {t("disclaimer.title", DISCLAIMER_TITLE)}
            </h2>
          </div>

          {/* the disclaimer itself — scrolls inside the dialog */}
          <div className="grow overflow-y-auto overscroll-contain px-6 py-6 md:px-10 md:py-8">
            <p className="text-xs font-medium uppercase leading-relaxed tracking-wide text-brand-ink" data-cms-key="disclaimer.lead">
              {t("disclaimer.lead", DISCLAIMER_LEAD)}
            </p>
            <div className="mt-6 space-y-5">
              {paras.map((p, i) => (
                <p key={i} className="text-sm leading-relaxed text-brand-slate" data-cms-key={`disclaimer.p.${i}`}>
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-8 space-y-5 border-t border-brand-line pt-8">
              {confirmParas.map((p, i) => (
                <p key={i} className="text-sm leading-relaxed text-brand-slate" data-cms-key={`disclaimer.confirm.${i}`}>
                  {p}
                </p>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-brand-line bg-brand-surface px-6 py-5 sm:flex-row sm:justify-end md:px-10">
            <button
              type="button"
              onClick={(e) => {
                if (editing && (e.target as HTMLElement).closest("[data-cms-key]")) return;
                setDeclined(true);
              }}
              className="btn-secondary btn-sm justify-center"
            >
              <span data-cms-key="disclaimer.decline.label">
                {t("disclaimer.decline.label", DISCLAIMER_DECLINE_LABEL)}
              </span>
            </button>
            <button
              type="button"
              onClick={(e) => {
                if (editing && (e.target as HTMLElement).closest("[data-cms-key]")) return;
                onConfirm();
              }}
              className="btn-primary btn-sm justify-center"
            >
              <span data-cms-key="disclaimer.confirm.label">
                {t("disclaimer.confirm.label", DISCLAIMER_CONFIRM_LABEL)}
              </span>
            </button>
          </div>

          {editing && (
            <div className="border-t border-brand-line bg-brand-veil px-6 py-5 md:px-10">
              <p className="mono-meta uppercase text-brand-muted">
                Shown after &ldquo;{t("disclaimer.decline.label", DISCLAIMER_DECLINE_LABEL)}&rdquo; — editable here
              </p>
              <p className="mt-2 text-sm leading-relaxed text-brand-ink" data-cms-key="disclaimer.blocked">
                {t("disclaimer.blocked", DISCLAIMER_BLOCKED)}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
